// M13.4 — per-channel revenue stacked area chart for EcommerceSection.
//
// Differs from SourceMixChart in two important ways:
//   1. NOT normalised to 100% — bands are stacked on absolute revenue
//      ($), so the sum of all bands at a given date equals total
//      revenue for that date.
//   2. Uses the existing channel palette so the band colours line up
//      with the SourceMixSection table directly above.
//
// Top 8 channels by total revenue become individual bands; everything
// else collapses into a grey "Other" band so the legend stays legible.

import { useEffect, useMemo, useRef, useState } from "react";
import { bisector, max, sum } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack, type SeriesPoint } from "d3-shape";
import type { ReportEcommerceTrendRow } from "@/lib/api";
import { channelColor, OTHER_CHANNEL_COLOR } from "./channel-colors";
import type { Granularity } from "./granularity";
import { formatCurrencyOrNumber } from "./format-currency";

const TOP_BAND_LIMIT = 8;
const OTHER_LABEL = "Other";

const DEFAULT_HEIGHT = 240;
const MARGIN = { top: 20, right: 16, bottom: 28, left: 56 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";

export interface EcommerceChartProps {
  rows: ReportEcommerceTrendRow[];
  granularity: Granularity;
  /** ISO 4217 currency code from `meta.currencyCode`. Null = plain
   *  numbers on the Y axis + tooltip. */
  currencyCode: string | null | undefined;
  ariaLabel: string;
}

export function EcommerceChart({
  rows,
  granularity,
  currencyCode,
  ariaLabel,
}: EcommerceChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(Math.max(320, Math.floor(w)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = DEFAULT_HEIGHT;
  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = height - MARGIN.top - MARGIN.bottom;

  const { keys, byDate, totalsByChannel } = useMemo(() => {
    const totals = new Map<string, number>();
    const dateSet = new Set<string>();
    for (const r of rows) {
      totals.set(r.channel, (totals.get(r.channel) ?? 0) + r.revenue);
      dateSet.add(r.date);
    }
    const sortedChannels = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([ch]) => ch);
    const topChannels = sortedChannels.slice(0, TOP_BAND_LIMIT);
    const otherChannels = new Set(sortedChannels.slice(TOP_BAND_LIMIT));
    const includeOther = otherChannels.size > 0;

    const dates = Array.from(dateSet).sort();
    const keyList = includeOther ? [...topChannels, OTHER_LABEL] : topChannels;

    interface StackRow {
      date: string;
      cells: Record<string, number>;
    }
    const byDate: StackRow[] = dates.map((d) => {
      const cells: Record<string, number> = {};
      for (const k of keyList) cells[k] = 0;
      return { date: d, cells };
    });
    const indexByDate = new Map(byDate.map((r, i) => [r.date, i]));
    for (const r of rows) {
      const idx = indexByDate.get(r.date);
      if (idx === undefined) continue;
      const key = otherChannels.has(r.channel) ? OTHER_LABEL : r.channel;
      byDate[idx].cells[key] = (byDate[idx].cells[key] ?? 0) + r.revenue;
    }
    return { keys: keyList, byDate, totalsByChannel: totals };
  }, [rows]);

  const flatCells = useMemo(() => byDate.map((r) => ({ ...r.cells })), [byDate]);

  const { stacked, xScale, yScale, xTicks, yTickValues } = useMemo(() => {
    if (byDate.length === 0) {
      return {
        stacked: [],
        xScale: scaleTime().range([0, innerW]),
        yScale: scaleLinear().range([innerH, 0]),
        xTicks: [] as Date[],
        yTickValues: [] as number[],
      };
    }
    // No `stackOffsetExpand` here — we want absolute dollars, not 100%.
    const stackGen = d3Stack<Record<string, number>>().keys(keys);
    const stacked = stackGen(flatCells);
    const dates = byDate.map((d) => new Date(`${d.date}T00:00:00Z`));
    const [xMin, xMax] = [dates[0], dates[dates.length - 1]];
    const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
    // Add 10% headroom so the topmost band doesn't kiss the upper border.
    const yMax = yMaxRaw === 0 ? 1 : yMaxRaw * 1.1;
    const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
    const yScale = scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);
    const xTickCount = granularity === "day" ? 6 : granularity === "week" ? 5 : 4;
    return {
      stacked,
      xScale,
      yScale,
      xTicks: xScale.ticks(xTickCount),
      yTickValues: yScale.ticks(4),
    };
  }, [byDate, flatCells, keys, innerW, innerH, granularity]);

  const areaGen = useMemo(() => {
    return d3Area<SeriesPoint<Record<string, number>>>()
      .x((_, i) => (byDate[i] ? xScale(new Date(`${byDate[i].date}T00:00:00Z`)) : 0))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]));
  }, [xScale, yScale, byDate]);

  // ---- hover --------------------------------------------------------------
  const dateObjs = useMemo(
    () => byDate.map((d) => ({ date: d.date, dateObj: new Date(`${d.date}T00:00:00Z`) })),
    [byDate],
  );
  const bisectDate = useMemo(
    () => bisector<{ date: string; dateObj: Date }, Date>((d) => d.dateObj).center,
    [],
  );

  const onMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xInside = event.clientX - rect.left - MARGIN.left;
    if (xInside < 0 || xInside > innerW) {
      setHoverX(null);
      return;
    }
    setHoverX(xInside);
  };

  const hoverInfo = useMemo(() => {
    if (hoverX === null || dateObjs.length === 0) return null;
    const dateAtX = xScale.invert(hoverX);
    const idx = bisectDate(dateObjs, dateAtX);
    const ref = dateObjs[Math.max(0, Math.min(dateObjs.length - 1, idx))];
    const cellRow = byDate.find((r) => r.date === ref.date);
    if (!cellRow) return null;
    const cx = xScale(ref.dateObj);
    const total = sum(keys, (k) => cellRow.cells[k] ?? 0);
    const series = keys.map((k) => {
      const v = cellRow.cells[k] ?? 0;
      return { label: k, value: v };
    });
    return { cx, date: ref.date, series, total };
  }, [hoverX, dateObjs, xScale, bisectDate, byDate, keys]);

  if (rows.length === 0 || byDate.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-56 items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-500"
      >
        No ecommerce activity in the selected window.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="ts-chart-reveal font-sans"
      >
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {/* Y gridlines + tick labels (currency) */}
          {yTickValues.map((v) => {
            const y = yScale(v);
            return (
              <g key={`y-${v}`}>
                <line x1={0} y1={y} x2={innerW} y2={y} stroke={GRID_COLOR} />
                <text x={-8} y={y + 3} fontSize={10} fill={AXIS_COLOR} textAnchor="end">
                  {formatCurrencyOrNumber(v, currencyCode, true)}
                </text>
              </g>
            );
          })}
          {/* X ticks */}
          {xTicks.map((d, i) => (
            <g key={`x-${i}`}>
              <line x1={xScale(d)} y1={innerH} x2={xScale(d)} y2={innerH + 4} stroke={AXIS_COLOR} />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill={AXIS_COLOR}
                textAnchor="middle"
              >
                {formatXTick(d, granularity)}
              </text>
            </g>
          ))}
          {/* Stacked bands */}
          {stacked.map((s) => {
            const d = areaGen(s);
            if (!d) return null;
            const fill = s.key === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(s.key);
            return (
              <path
                key={s.key}
                d={d}
                fill={fill}
                stroke="#ffffff"
                strokeWidth={0.5}
                opacity={0.92}
              />
            );
          })}
          {/* Hover crosshair */}
          {hoverInfo ? (
            <line
              x1={hoverInfo.cx}
              y1={0}
              x2={hoverInfo.cx}
              y2={innerH}
              stroke={AXIS_COLOR}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          ) : null}
          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHoverX(null)}
          />
        </g>
      </svg>

      {/* Legend — chips for every visible band */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {keys.map((k) => {
          const total = totalsByChannel.get(k) ?? 0;
          return (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{
                  backgroundColor: k === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(k),
                }}
              />
              <span className="font-semibold text-neutral-950">{k}</span>
              {k !== OTHER_LABEL ? (
                <span className="tabular-nums text-neutral-500">
                  {formatCurrencyOrNumber(total, currencyCode, true)}
                </span>
              ) : null}
            </span>
          );
        })}
      </div>

      {hoverInfo ? (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm"
          style={{
            left: `${hoverInfo.cx + MARGIN.left}px`,
            top: `${MARGIN.top}px`,
            transform: `translate(-50%, -110%)`,
          }}
        >
          <p className="font-semibold text-neutral-950">{formatTooltipDate(hoverInfo.date)}</p>
          <p className="mt-0.5 text-[11px] text-neutral-500">
            Total: {formatCurrencyOrNumber(hoverInfo.total, currencyCode)}
          </p>
          <ul className="mt-1 space-y-0.5">
            {hoverInfo.series
              .filter((s) => s.value > 0)
              .sort((a, b) => b.value - a.value)
              .map((s) => (
                <li key={s.label} className="flex items-center gap-2 whitespace-nowrap">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor:
                        s.label === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(s.label),
                    }}
                    aria-hidden="true"
                  />
                  <span className="text-neutral-600">{s.label}</span>
                  <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                    {formatCurrencyOrNumber(s.value, currencyCode)}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatXTick(d: Date, granularity: Granularity): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatTooltipDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}
